import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Brain, MessageSquare, ArrowRight, Target, Award, CheckCircle, ArrowLeft } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import ChatBot from '../ChatBot';

const translations = {
  es: {
    title: 'Mentor IA',
    subtitle: 'Tu asistente personal para la experiencia laboral virtual',
    features: {
      guidance: {
        title: 'Orientación Personalizada',
        description: 'Recibe ayuda contextual y recomendaciones basadas en tu rol y tareas actuales'
      },
      support: {
        title: 'Soporte 24/7',
        description: 'Asistencia disponible en cualquier momento para resolver dudas y problemas'
      },
      learning: {
        title: 'Aprendizaje Continuo',
        description: 'Sugerencias de mejora y recursos de aprendizaje personalizados'
      }
    },
    cta: 'Chatear con el mentor',
    recentActivity: 'Actividad Reciente',
    suggestions: 'Sugerencias Personalizadas',
    progress: 'Tu Progreso',
    actions: {
      back: 'Volver al Dashboard',
      startNow: 'Comenzar ahora'
    },
    status: {
      completed: 'Completado',
      inProgress: 'En progreso'
    }
  },
  en: {
    title: 'AI Mentor',
    subtitle: 'Your personal assistant for the virtual work experience',
    features: {
      guidance: {
        title: 'Personalized Guidance',
        description: 'Get contextual help and recommendations based on your role and current tasks'
      },
      support: {
        title: '24/7 Support',
        description: 'Assistance available anytime to resolve doubts and issues'
      },
      learning: {
        title: 'Continuous Learning',
        description: 'Personalized improvement suggestions and learning resources'
      }
    },
    cta: 'Chat with mentor',
    recentActivity: 'Recent Activity',
    suggestions: 'Personalized Suggestions',
    progress: 'Your Progress',
    actions: {
      back: 'Back to Dashboard',
      startNow: 'Start now'
    },
    status: {
      completed: 'Completed',
      inProgress: 'In progress'
    }
  }
};

const AIMentor = () => {
  const [showChatbot, setShowChatbot] = useState(false);
  const { language } = useLanguage();
  const t = translations[language];
  const navigate = useNavigate();

  // Mock data for recent activity
  const recentActivity = [
    {
      id: 1,
      type: 'task',
      title: language === 'es' ? 'Análisis de Datos CRM' : 'CRM Data Analysis',
      status: 'completed',
      feedback: language === 'es' ? 'Excelente trabajo en segmentación de clientes' : 'Excellent work on customer segmentation',
      timestamp: language === 'es' ? 'hace 2h' : '2h ago'
    },
    {
      id: 2,
      type: 'learning',
      title: language === 'es' ? 'Fundamentos de Gestión de Proyectos' : 'Project Management Basics',
      status: 'in-progress',
      progress: 65,
      timestamp: language === 'es' ? 'hace 1d' : '1d ago'
    }
  ];

  // Mock data for suggestions
  const suggestions = [
    {
      id: 1,
      title: language === 'es' ? 'Completar Formación ERP' : 'Complete ERP Training',
      description: language === 'es' ? 'Mejora tu comprensión de la planificación de recursos empresariales' : 'Enhance your understanding of enterprise resource planning',
      priority: 'high'
    },
    {
      id: 2,
      title: language === 'es' ? 'Revisar Tickets de Soporte' : 'Review Support Tickets',
      description: language === 'es' ? 'Practica el manejo de escenarios de soporte al cliente' : 'Practice handling customer support scenarios',
      priority: 'medium'
    }
  ];

  return (
    <div className="space-y-6">
      {/* AI Mentor Header */}
      <div className="dark-card p-6">
        <div className="flex items-center justify-between mb-6">
          <button
            onClick={() => navigate('/dcvee/dashboard')}
            className="flex items-center text-foreground/70 hover:text-foreground"
          >
            <ArrowLeft className="h-5 w-5 mr-2" />
            {t.actions.back}
          </button>
        </div>

        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <Brain className="h-8 w-8 text-accent" />
            <div className="ml-4">
              <h2 className="text-xl font-bold text-foreground">{t.title}</h2>
              <p className="text-foreground/70">{t.subtitle}</p>
            </div>
          </div>
          <button
            onClick={() => setShowChatbot(true)}
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-accent hover:bg-accent/90"
          >
            <MessageSquare className="h-4 w-4 mr-2" />
            {t.cta}
          </button>
        </div>

        <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4">
          {Object.entries(t.features).map(([key, feature]) => (
            <div key={key} className="bg-secondary/30 rounded-lg p-4">
              <h3 className="font-medium text-foreground">{feature.title}</h3>
              <p className="mt-1 text-sm text-foreground/70">{feature.description}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Recent Activity */}
      <div className="dark-card p-6">
        <h3 className="text-lg font-medium text-foreground mb-4">{t.recentActivity}</h3>
        <div className="space-y-4">
          {recentActivity.map((activity) => (
            <div key={activity.id} className="flex items-start">
              {activity.type === 'task' ? (
                <Target className="h-5 w-5 text-accent mt-1" />
              ) : (
                <Award className="h-5 w-5 text-accent mt-1" />
              )}
              <div className="ml-3">
                <p className="font-medium text-foreground">{activity.title}</p>
                <div className="flex items-center mt-1">
                  {activity.status === 'completed' ? (
                    <CheckCircle className="h-4 w-4 text-green-500 mr-1" />
                  ) : (
                    <div className="h-2 w-24 bg-secondary rounded-full mr-2">
                      <div 
                        className="h-2 bg-accent rounded-full" 
                        style={{ width: `${activity.progress}%` }}
                      ></div>
                    </div>
                  )}
                  <span className="text-sm text-foreground/70">{activity.timestamp}</span>
                </div>
                {activity.feedback && (
                  <p className="text-sm text-foreground/70 mt-1">{activity.feedback}</p>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Personalized Suggestions */}
      <div className="dark-card p-6">
        <h3 className="text-lg font-medium text-foreground mb-4">{t.suggestions}</h3>
        <div className="space-y-4">
          {suggestions.map((suggestion) => (
            <div key={suggestion.id} className="flex items-start">
              <div className={`h-2 w-2 rounded-full mt-2 ${
                suggestion.priority === 'high' ? 'bg-red-500' : 'bg-yellow-500'
              }`}></div>
              <div className="ml-3">
                <p className="font-medium text-foreground">{suggestion.title}</p>
                <p className="text-sm text-foreground/70">{suggestion.description}</p>
                <button className="mt-2 text-accent hover:text-accent/90 text-sm flex items-center">
                  {t.actions.startNow}
                  <ArrowRight className="ml-1 h-4 w-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Chatbot Modal */}
      {showChatbot && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="w-full max-w-2xl">
            <ChatBot 
              role="dcvee-mentor"
              onClose={() => setShowChatbot(false)}
              fullScreen
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default AIMentor;